import React from "react";
import { Drawer } from "antd";
import { convertUtcToLocal, firstUpperCaseWordParser } from "../../../../library/utils";
import { getUniqueVehicleOfInterest, statusColor, getVehicleName } from "../engagementHelpers";

const SeperatorLine = () => {
  return (
    <div
      style={{
        background: "var(--Colors-Neutral-Border-colorBorder, #D9D9D9)",
        height: "1px",
        margin: "8px 0",
      }}
    />
  );
};
const InvoiceDetailsDrawer = ({
  open,
  onClose,
  invoiceData,
  title = "Invoice details",
  placement = "right",
  width = 525,
  className = "invoice-details-drawer",
}) => {
  // Helper function to get pricing data from multiple sources
  const getPricingData = () => {
    // First, try to get from extraData.Units array (legacy format)
    if (invoiceData?.extraData?.Units && invoiceData.extraData.Units.length > 0) {
      const unit = invoiceData.extraData.Units[0];
      // Ensure all values are numbers for the legacy format as well
      return {
        Msrp: Number(unit.Msrp) || 0,
        Dsp: Number(unit.Dsp) || 0,
        UnitSoldPrice: Number(unit.UnitSoldPrice) || 0,
        UnitSoldCost: Number(unit.UnitSoldCost) || 0,
        Tax: Number(unit.Tax) || 0,
        Fees: Number(unit.Fee) || 0, // Note: legacy format uses 'Fee' not 'Fees'
        AmtFinanced: Number(unit.AmtFinanced) || 0,
        Payment: Number(unit.Payment) || 0,
        TradeAll: Number(unit.TradeAll) || 0,
        TradeAcv: Number(unit.TradeAcv) || 0,
        TradePayoff: unit.TradePayoff ? Number(unit.TradePayoff) : 0,
      };
    }

    // Fallback to ziMajorUnitInvoiceItems (current format)
    if (invoiceData?.ziMajorUnitInvoiceItems && invoiceData.ziMajorUnitInvoiceItems.length > 0) {
      const firstItem = invoiceData.ziMajorUnitInvoiceItems[0];
      return {
        Msrp: Number(firstItem.msrp) || 0,
        Dsp: Number(firstItem.dsp) || 0,
        UnitSoldPrice: Number(firstItem.unitSoldPrice) || 0,
        UnitSoldCost: Number(firstItem.unitSoldCost) || 0,
        Tax: Number(firstItem.tax) || 0,
        Fees: Number(firstItem.fee) || 0,
        AmtFinanced: Number(firstItem.amtFinanced) || 0,
        Payment: Number(firstItem.payment) || 0,
        TradeAll: Number(firstItem.tradeAllowance) || 0,
        TradeAcv: Number(firstItem.tradeAcv) || 0,
        TradePayoff: firstItem.tradePayoff ? Number(firstItem.tradePayoff) : 0,
      };
    }

    return null;
  };

  const pricing = getPricingData();

  const buyVehicles = (invoiceData?.ziMajorUnitInvoiceItems || [])?.filter(
    item => item.leadVehicleOfInterest.customerInterest === "BUY"
  );
  // const buyVehicleName = getVehicleName(buyVehicle?.leadVehicleOfInterest);

  const tradeVehicles = (invoiceData?.ziMajorUnitInvoiceItems || [])?.filter(
    item => item.leadVehicleOfInterest.customerInterest === "TRADE"
  );
  // const tradeVehicleName = getVehicleName(tradeVehicle?.leadVehicleOfInterest);
  return (
    <Drawer
      title={title}
      placement={placement}
      width={width}
      onClose={onClose}
      open={open}
      className={className}
    >
      {invoiceData && (
        <div className="space-y-6 pt-4">
          {/* Header */}
          <div className="border-b border-grey-200 pb-2 pl-2">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-[20px] font-[600] text-black-600">
                Invoice #: {invoiceData.ziDealNo}
              </h2>
              <span
                className="text-xs px-2 py-1"
                style={{
                  borderRadius: "var(--Components-Tag-Global-borderRadiusSM, 4px)",
                  border:
                    "var(--Components-Tag-Global-lineWidth, 1px) solid var(--Components-Tag-Global-colorBorder, #D9D9D9)",
                  background: "var(--Components-Tag-Component-defaultBg, rgba(0, 0, 0, 0.02))",
                  color: "var(--Components-Tag-Component-defaultColor, rgba(0, 0, 0, 0.88))",
                }}
              >
                {invoiceData.ziDealStatus}
              </span>
            </div>
            <div className="text-xs text-grey-500 space-y-1">
              <div>
                Created on: {convertUtcToLocal(invoiceData.createdAt, "MMM DD, YYYY, hh:mm A")}
              </div>
              <div>
                Updated on: {convertUtcToLocal(invoiceData.updatedAt, "MMM DD, YYYY, hh:mm A")}
              </div>
            </div>
          </div>
          <div
            className="border-b border-grey-200 pb-2 pl-2"
            style={{
              borderRadius: "8px",
              background: "var(--Colors-Neutral-Bg-colorBgLayout, #F5F5F5)",
            }}
          >
            {/* Buy Vehicles */}
            {buyVehicles && buyVehicles.length > 0 && (
              <div className="py-1">
                {buyVehicles.map((buyVehicle, index) => {
                  const buyVehicleName = getVehicleName(buyVehicle?.leadVehicleOfInterest);
                  return (
                    <div key={`buy-vehicle-${index}`} className="p-2 rounded">
                      <h4 className="text-sm font-[600] text-grey-800 mb-2">
                        <span className="text-blue-600">
                          (
                          {firstUpperCaseWordParser(
                            buyVehicle.leadVehicleOfInterest.customerInterest
                          )}
                          )
                        </span>{" "}
                        {buyVehicleName}
                      </h4>
                      <div className="text-xs text-grey-600 space-y-1">
                        <div>VIN: {buyVehicle.leadVehicleOfInterest.vin || "N/A"}</div>
                        <div>Stock: {buyVehicle.leadVehicleOfInterest.stockNumber || "N/A"}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Separator Line */}
            {buyVehicles && buyVehicles.length > 0 && tradeVehicles && tradeVehicles.length > 0 && (
              <SeperatorLine />
            )}

            {/* Trade Vehicles */}
            {tradeVehicles && tradeVehicles.length > 0 && (
              <div className="py-1">
                {tradeVehicles.map((tradeVehicle, index) => {
                  const tradeVehicleName = getVehicleName(tradeVehicle?.leadVehicleOfInterest);
                  return (
                    <div key={`trade-vehicle-${index}`} className="p-2 rounded">
                      <h4 className="text-sm font-[600] text-grey-800 mb-2">
                        <span className="text-orange-600">
                          (
                          {firstUpperCaseWordParser(
                            tradeVehicle.leadVehicleOfInterest.customerInterest
                          )}
                          )
                        </span>{" "}
                        {tradeVehicleName}
                      </h4>
                      <div className="text-xs text-grey-600 space-y-1">
                        <div>VIN: {tradeVehicle.leadVehicleOfInterest.vin || "N/A"}</div>
                        <div>Stock: {tradeVehicle.leadVehicleOfInterest.stockNumber || "N/A"}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {/* Pricing */}
          <div className="py-4  border-1 border-grey-200 p-2 border rounded-md">
            {pricing && (
              <div className="pl-2">
                <h3 className="text-sm font-[600] text-grey-800 mb-3">Pricing</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-grey-600">MSRP</span>
                    <span className="text-grey-800">${pricing?.Msrp?.toFixed(2) || "0.00"}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-grey-600">DSP</span>
                    <span className="text-grey-800">${pricing?.Dsp?.toFixed(2) || "0.00"}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-grey-600">Sold Price</span>
                    <span className="text-grey-800">
                      ${pricing.UnitSoldPrice?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-grey-600">Sold Cost</span>
                    <span className="text-grey-800">
                      ${pricing.UnitSoldCost?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-grey-600">Tax</span>
                    <span className="text-grey-800">${pricing.Tax?.toFixed(2) || "0.00"}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-grey-600">Fees</span>
                    <span className="text-grey-800">${pricing.Fees?.toFixed(2) || "0.00"}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Separator Line */}
            {pricing &&
              (pricing?.TradeAll > 0 ||
                pricing?.TradeAcv > 0 ||
                pricing?.TradePayoff > 0 ||
                (tradeVehicles && tradeVehicles.length > 0)) && <SeperatorLine />}

            {/* Trade */}
            {(pricing?.TradeAll > 0 ||
              pricing?.TradeAcv > 0 ||
              pricing?.TradePayoff > 0 ||
              (tradeVehicles && tradeVehicles.length > 0)) && (
              <div className="pl-2">
                <h3 className="text-sm font-[600] text-grey-800 mb-3">Trade</h3>

                {/* Trade Financial Details */}
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-grey-600">Trade Allowance</span>
                    <span className="text-grey-800">${pricing.TradeAll?.toFixed(2) || "0.00"}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-grey-600">Trade ACV</span>
                    <span className="text-grey-800">${pricing.TradeAcv?.toFixed(2) || "0.00"}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-grey-600">Trade Payoff</span>
                    <span className="text-grey-800">
                      {pricing.TradePayoff === 0 || pricing.TradePayoff === null
                        ? "-"
                        : `$${pricing.TradePayoff?.toFixed(2) || "0.00"}`}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Separator Line */}
            {(pricing?.TradeAll > 0 ||
              pricing?.TradeAcv > 0 ||
              pricing?.TradePayoff > 0 ||
              (tradeVehicles && tradeVehicles.length > 0)) &&
              (pricing?.AmtFinanced > 0 || pricing?.Payment > 0) && <SeperatorLine />}

            {/* Finance */}
            {(pricing?.AmtFinanced > 0 || pricing?.Payment > 0) && (
              <div className="pl-2">
                <h3 className="text-sm font-[600] text-grey-800 mb-3">Finance</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-grey-600">Amount Financed</span>
                    <span className="text-grey-800">
                      ${pricing.AmtFinanced?.toFixed(2) || "0.00"}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-grey-600">Payment</span>
                    <span className="text-grey-800">${pricing.Payment?.toFixed(2) || "0.00"}</span>
                  </div>
                </div>
              </div>
            )}
            <SeperatorLine />
            {/* Total Invoice Amount */}
            <div className="border-t border-grey-200 pt-2 pl-2">
              <div className="flex justify-between items-center">
                <h3 className="text-[14px] font-[600] text-grey-600">Total Invoice Amount</h3>
                <span className="text-[14px] font-[600] text-grey-600">
                  ${(invoiceData?.invoiceTotal || 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default InvoiceDetailsDrawer;
